import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Search, Building2, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { warehousesAPI } from '@/services/api';
import AddressForm from '@/components/AddressForm';

const initialFormData = {
  code: '',
  name: '',
  address: {
    address_line1: '',
    address_line2: '',
    country: 'India',
    state: '',
    district: '',
    area: '',
    pincode: ''
  },
  contact: {
    phone: '',
    email: '',
    alternate_phone: ''
  }
};

const WarehousesPage = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await warehousesAPI.list({ search });
      setWarehouses(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch warehouses',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const handleOpenDialog = (warehouse = null) => {
    if (warehouse) {
      setSelectedWarehouse(warehouse);
      setFormData({
        code: warehouse.code,
        name: warehouse.name,
        address: {
          address_line1: warehouse.address?.address_line1 || '',
          address_line2: warehouse.address?.address_line2 || '',
          country: warehouse.address?.country || 'India',
          state: warehouse.address?.state || '',
          district: warehouse.address?.district || warehouse.address?.city || '',
          area: warehouse.address?.area || '',
          pincode: warehouse.address?.pincode || ''
        },
        contact: warehouse.contact || initialFormData.contact
      });
    } else {
      setSelectedWarehouse(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast({
        title: 'Validation Error',
        description: 'Code and Name are required',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.address.state || !formData.address.district || !formData.address.pincode) {
      toast({
        title: 'Validation Error',
        description: 'State, District, and Pincode are required',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      // Map district to city for backward compatibility
      const payload = {
        ...formData,
        address: {
          ...formData.address,
          city: formData.address.district // Map district to city field
        }
      };

      if (selectedWarehouse) {
        await warehousesAPI.update(selectedWarehouse.id, payload);
        toast({ title: 'Success', description: 'Warehouse updated successfully' });
      } else {
        await warehousesAPI.create(payload);
        toast({ title: 'Success', description: 'Warehouse created successfully' });
      }
      setDialogOpen(false);
      fetchWarehouses();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save warehouse',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await warehousesAPI.delete(selectedWarehouse.id);
      toast({ title: 'Success', description: 'Warehouse deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchWarehouses();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete warehouse',
        variant: 'destructive'
      });
    }
  };

  const handleAddressChange = (newAddress) => {
    setFormData(prev => ({ ...prev, address: newAddress }));
  };

  const updateContactField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    const parts = [];
    if (addr.area) parts.push(addr.area);
    if (addr.district || addr.city) parts.push(addr.district || addr.city);
    if (addr.state) parts.push(addr.state);
    if (addr.pincode) parts.push(addr.pincode);
    return parts.join(', ');
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Warehouses</h1>
          <p className="text-gray-500">Manage your distribution centers</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-200"
          data-testid="add-warehouse-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Warehouse
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search warehouses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-300 text-gray-800 focus:border-blue-500"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Warehouses Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : warehouses.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No warehouses found</p>
            <Button
              onClick={() => handleOpenDialog()}
              variant="link"
              className="text-blue-600 mt-2"
            >
              Add your first warehouse
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {warehouses.map(warehouse => (
            <Card
              key={warehouse.id}
              className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`warehouse-card-${warehouse.code}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-gray-800 text-lg">{warehouse.name}</CardTitle>
                    <p className="text-sm text-blue-600 font-medium mt-1">{warehouse.code}</p>
                  </div>
                  <Badge className={warehouse.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                    {warehouse.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {warehouse.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{formatAddress(warehouse.address)}</span>
                  </div>
                )}
                {warehouse.contact?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">{warehouse.contact.phone}</span>
                  </div>
                )}
                {warehouse.contact?.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">{warehouse.contact.email}</span>
                  </div>
                )}
                <div className="flex gap-2 pt-3 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-50"
                    onClick={() => handleOpenDialog(warehouse)}
                    data-testid={`edit-warehouse-${warehouse.code}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-500 hover:bg-red-50"
                    onClick={() => {
                      setSelectedWarehouse(warehouse);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-warehouse-${warehouse.code}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">
              {selectedWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., WH001"
                  className="bg-gray-50 border-gray-300"
                  disabled={!!selectedWarehouse}
                  data-testid="warehouse-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Warehouse Name"
                  className="bg-gray-50 border-gray-300"
                  data-testid="warehouse-name-input"
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
              <h3 className="text-sm font-medium text-blue-800 mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Address Details
              </h3>
              <AddressForm
                address={formData.address}
                onChange={handleAddressChange}
              />
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Phone</Label>
                <Input
                  value={formData.contact.phone}
                  onChange={(e) => updateContactField('phone', e.target.value)}
                  placeholder="Phone number"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Email</Label>
                <Input
                  value={formData.contact.email}
                  onChange={(e) => updateContactField('email', e.target.value)}
                  placeholder="Email address"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-gray-300 text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="save-warehouse-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate Warehouse?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate the warehouse "{selectedWarehouse?.name}". This action can be reversed later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WarehousesPage;
